import { useCallback } from 'react';
import { useAuth } from '../auth/AuthContext';
import { useNavigate } from 'react-router-dom';
import { REFRESH_TOKEN_URL } from './urls';

let refreshPromise: Promise<string | null> | null = null;
let hasRedirected = false;

const startRefresh = async (): Promise<string | null> => {
	try {
		const res = await fetch(REFRESH_TOKEN_URL, { method: 'POST', credentials: 'include' });
		if (!res.ok) return null;
		const json = await res.json();
		const access = (json?.access as string) || null;
		return access;
	} catch {
		return null;
	}
};

export const useApiFetch = () => {
	const { token, logout, setToken } = useAuth() as any;
	const navigate = useNavigate();

	const apiFetch = useCallback(async (input: RequestInfo | URL, init?: RequestInit) => {
		const doRequest = async (bearer?: string) => {
			const headers = new Headers(init?.headers || {});
			const authToken = bearer ?? token;
			if (authToken) {
				headers.set('Authorization', `Bearer ${authToken}`);
			}
			if (!headers.has('Content-Type') && init?.method && init.method.toUpperCase() !== 'GET') {
				headers.set('Content-Type', 'application/json');
			}
			return fetch(input, { ...init, headers, credentials: init?.credentials });
		};

		let response = await doRequest();
		if (response.status !== 401) return response;

		if (!refreshPromise) {
			refreshPromise = startRefresh().finally(() => {
				setTimeout(() => { refreshPromise = null; }, 0);
			});
		}

		const newAccess = await refreshPromise;
		if (newAccess) {
			setToken(newAccess);
			response = await doRequest(newAccess);
			if (response.status !== 401) return response;
		}

		if (!hasRedirected) {
			hasRedirected = true;
			logout();
			navigate('/login/', { replace: true });
			setTimeout(() => { hasRedirected = false; }, 2000);
		}
		throw new Error('Unauthorized');
	}, [token, logout, setToken, navigate]);

	return apiFetch;
}; 